import React, { useId, useState } from 'react';
import { formatClockTime } from './format';
import type { ChartSeries } from './types';

/**
 * Series colours are literal hex, not CSS variables.
 *
 * The app ships a dark skin (`/css/dark.css`, loaded after the bundle) which
 * overrides the page background but *not* `--accent-color` - it stays the same
 * dark navy in both themes, and is near-invisible on the dark background. There
 * is no existing theme-neutral accent to borrow, so these are picked by hand to
 * clear roughly 3:1 against both backgrounds. Everything that isn't a series
 * line does use the theme variables, so no dark.css change is needed.
 */
export const CHART_COLORS = {
  primary: '#4e9be0',
  secondary: '#e0913c',
  read: '#5fbf7a',
  write: '#d0607a',
} as const;

const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 200;
const PLOT_LEFT = 56;
const PLOT_RIGHT = VIEW_WIDTH - 12;
const PLOT_TOP = 8;
const PLOT_BOTTOM = VIEW_HEIGHT - 22;
const PLOT_WIDTH = PLOT_RIGHT - PLOT_LEFT;
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;

/** Five lines including both edges, so a percentage axis reads 0/25/50/75/100. */
const GRID_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];

export interface TimeSeriesChartProps {
  title: string;
  /** Unix seconds, ascending. One per sample, already resolved to end_time. */
  timestamps: number[];
  /** Parallel to timestamps. Greater than 1 marks a downsampled point. */
  sampleCounts: number[];
  series: ChartSeries[];
  /** Fixed axis maximum, e.g. 100 for a percentage. Omitted means auto-scale. */
  yMax?: number;
  /** Formats the readout, the y-axis ticks, the popover and the summary. */
  formatValue: (value: number) => string;
}

/** Round an axis maximum up to 1, 2 or 5 times a power of ten. */
function niceMax(value: number): number {
  // Floored at 1 so an all-zero series still gets a non-degenerate axis rather
  // than dividing by zero on every point.
  if (!(value > 0)) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return step * magnitude;
}

/**
 * Split a series into runs of consecutive non-null points.
 *
 * One polyline per run, so a null breaks the line instead of drawing a straight
 * segment across a gap that never had data in it.
 */
function contiguousRuns(points: (number | null)[]): { index: number; value: number }[][] {
  const runs: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];

  points.forEach((value, index) => {
    if (value === null || !Number.isFinite(value)) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }

    current.push({ index, value });
  });

  if (current.length) runs.push(current);

  return runs;
}

/**
 * A multi-series line chart drawn as plain SVG.
 *
 * The viewBox is fixed and nothing is ever measured - no ResizeObserver, no
 * getBoundingClientRect. That is a testability decision: jsdom returns zero from
 * every layout API, so a measurement-driven chart cannot be unit-tested at all,
 * whereas a pure data-to-coordinates function is fully assertable. CSS scales
 * the finished drawing to whatever width it lands in.
 */
export default function TimeSeriesChart({
  title,
  timestamps,
  sampleCounts,
  series,
  yMax,
  formatValue,
}: TimeSeriesChartProps) {
  // useId embeds colons, which are legal in an HTML id but awkward in a
  // selector. Stripped for the same reason PlayerInfoTabs strips them.
  const rawId = useId().replace(/:/g, '');
  const titleId = `${rawId}-title`;
  const descId = `${rawId}-desc`;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const values = series.flatMap((entry) =>
    entry.points.filter((point): point is number => point !== null && Number.isFinite(point)),
  );

  const hasData = timestamps.length > 0 && values.length > 0;
  const axisMax = yMax ?? niceMax(Math.max(...(values.length ? values : [0])));

  // A poll can shrink the window while a point is hovered - the processor folds
  // older entries into wider buckets - so the stored index is validated on every
  // render rather than reset from an effect.
  const activeIndex = hoverIndex !== null && hoverIndex < timestamps.length ? hoverIndex : null;

  const firstTime = timestamps[0] ?? 0;
  const lastTime = timestamps[timestamps.length - 1] ?? 0;
  const timeSpan = lastTime - firstTime;

  // Time-proportional, not index-based: entries are genuinely unevenly spaced
  // once the processor starts downsampling, and a wide bucket should look wide.
  function x(index: number): number {
    // A single sample, or a run of identical timestamps, has no span to divide
    // by - centre it rather than dividing by zero.
    if (timeSpan <= 0) return PLOT_LEFT + PLOT_WIDTH / 2;

    return PLOT_LEFT + ((timestamps[index] - firstTime) / timeSpan) * PLOT_WIDTH;
  }

  function y(value: number): number {
    const ratio = Math.min(Math.max(value / axisMax, 0), 1);

    return PLOT_BOTTOM - ratio * PLOT_HEIGHT;
  }

  /**
   * The invisible strip that claims the pointer for one sample: from halfway
   * back to the previous point to halfway on to the next.
   *
   * Hit testing is done with real elements rather than by converting pointer
   * coordinates into the viewBox, because that conversion needs
   * getScreenCTM/getBoundingClientRect - which jsdom does not implement, and
   * which would put hover behaviour beyond the reach of any unit test.
   */
  function band(index: number): { x: number; width: number } {
    const here = x(index);
    const left = index === 0 ? PLOT_LEFT : (x(index - 1) + here) / 2;
    const right = index === timestamps.length - 1 ? PLOT_RIGHT : (here + x(index + 1)) / 2;

    return { x: left, width: Math.max(right - left, 0) };
  }

  function moveHover(delta: number) {
    setHoverIndex((current) => {
      // Arrowing in from nothing starts at the newest reading, which is the one
      // an admin is looking for.
      if (current === null) return timestamps.length - 1;

      return Math.min(Math.max(current + delta, 0), timestamps.length - 1);
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<SVGSVGElement>) {
    if (!hasData) return;

    switch (event.key) {
      case 'ArrowLeft':
        moveHover(-1);
        break;
      case 'ArrowRight':
        moveHover(1);
        break;
      case 'Home':
        setHoverIndex(0);
        break;
      case 'End':
        setHoverIndex(timestamps.length - 1);
        break;
      case 'Escape':
        setHoverIndex(null);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  const latest = (() => {
    const points = series[0]?.points ?? [];

    for (let i = points.length - 1; i >= 0; i -= 1) {
      const point = points[i];
      if (point !== null && Number.isFinite(point)) return point;
    }

    return null;
  })();

  const aggregated = sampleCounts.some((count) => count > 1);

  // The accessible substitute for the picture. Deliberately carries real
  // numbers - it is the only way a screen reader gets at the shape of the data,
  // and it doubles as the most robust thing for a test to assert.
  const summary = hasData
    ? [
        `${timestamps.length} samples from ${formatClockTime(firstTime)} to ${formatClockTime(lastTime)}.`,
        latest === null ? '' : `Latest ${formatValue(latest)}.`,
        `Minimum ${formatValue(Math.min(...values))}.`,
        `Maximum ${formatValue(Math.max(...values))}.`,
        aggregated ? 'Earlier samples are averaged.' : '',
        'Use the arrow keys to read individual samples.',
      ]
        .filter(Boolean)
        .join(' ')
    : 'No data yet.';

  // Percentage across the whole viewBox, so the popover tracks the point once
  // CSS has scaled the drawing - the one place a coordinate leaves the SVG.
  const hoverPercent = activeIndex === null ? 0 : (x(activeIndex) / VIEW_WIDTH) * 100;
  const hoverAlign = hoverPercent < 20 ? 'start' : hoverPercent > 80 ? 'end' : 'center';

  return (
    <div className="system-stats__chart">
      <div className="system-stats__chart-header">
        <h4 className="system-stats__chart-title">{title}</h4>
        <span className="system-stats__readout">
          {latest === null ? '—' : formatValue(latest)}
        </span>
      </div>

      <div className="system-stats__chart-plot" onMouseLeave={() => setHoverIndex(null)}>
        <svg
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-labelledby={`${titleId} ${descId}`}
          // Focusable so the per-sample readout is reachable without a pointer.
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onBlur={() => setHoverIndex(null)}
        >
          <title id={titleId}>{title}</title>
          <desc id={descId}>{summary}</desc>

          {GRID_FRACTIONS.map((fraction) => {
            const lineY = PLOT_BOTTOM - fraction * PLOT_HEIGHT;

            return (
              <g key={fraction}>
                <line
                  x1={PLOT_LEFT}
                  y1={lineY}
                  x2={PLOT_RIGHT}
                  y2={lineY}
                  stroke="var(--page-text-color)"
                  strokeOpacity={fraction === 0 ? 0.4 : 0.15}
                />
                <text
                  x={PLOT_LEFT - 6}
                  y={lineY + 3}
                  textAnchor="end"
                  fontSize="9"
                  fill="var(--page-text-color)"
                  fillOpacity="0.7"
                >
                  {formatValue(axisMax * fraction)}
                </text>
              </g>
            );
          })}

          {!hasData && (
            <text
              x={PLOT_LEFT + PLOT_WIDTH / 2}
              y={PLOT_TOP + PLOT_HEIGHT / 2}
              textAnchor="middle"
              fontSize="11"
              fill="var(--page-text-color)"
              fillOpacity="0.6"
            >
              No data yet.
            </text>
          )}

          {hasData &&
            series.map((entry) =>
              contiguousRuns(entry.points).map((run) => {
                const key = `${entry.id}-${run[0].index}`;

                // A one-point polyline draws literally nothing, which reads as a
                // rendering bug rather than as a single reading.
                if (run.length === 1) {
                  return (
                    <circle
                      key={key}
                      cx={x(run[0].index)}
                      cy={y(run[0].value)}
                      r="2.5"
                      fill={entry.color}
                    />
                  );
                }

                return (
                  <polyline
                    key={key}
                    points={run.map((point) => `${x(point.index)},${y(point.value)}`).join(' ')}
                    fill="none"
                    stroke={entry.color}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              }),
            )}

          {/* Mark averaged points, so a flattened peak reads as reduced
              resolution rather than as a quiet stretch. */}
          {hasData &&
            series.flatMap((entry) =>
              entry.points.map((point, index) => {
                if (point === null || !Number.isFinite(point)) return null;
                if ((sampleCounts[index] ?? 1) <= 1) return null;

                return (
                  <circle
                    key={`${entry.id}-agg-${index}`}
                    className="system-stats__aggregated-point"
                    cx={x(index)}
                    cy={y(point)}
                    r="2"
                    fill={entry.color}
                  />
                );
              }),
            )}

          {activeIndex !== null && (
            <line
              className="system-stats__hover-guide"
              x1={x(activeIndex)}
              y1={PLOT_TOP}
              x2={x(activeIndex)}
              y2={PLOT_BOTTOM}
              stroke="var(--page-text-color)"
              strokeOpacity="0.35"
            />
          )}

          {activeIndex !== null &&
            series.map((entry) => {
              const point = entry.points[activeIndex];

              if (point === null || point === undefined || !Number.isFinite(point)) return null;

              return (
                <circle
                  key={`${entry.id}-hover`}
                  className="system-stats__hover-point"
                  cx={x(activeIndex)}
                  cy={y(point)}
                  r="3"
                  fill={entry.color}
                  stroke="var(--page-bg-color)"
                  strokeWidth="1.5"
                />
              );
            })}

          {hasData && (
            <>
              <text
                x={PLOT_LEFT}
                y={VIEW_HEIGHT - 6}
                textAnchor="start"
                fontSize="9"
                fill="var(--page-text-color)"
                fillOpacity="0.7"
              >
                {formatClockTime(firstTime)}
              </text>

              {timestamps.length >= 3 && (
                <text
                  x={PLOT_LEFT + PLOT_WIDTH / 2}
                  y={VIEW_HEIGHT - 6}
                  textAnchor="middle"
                  fontSize="9"
                  fill="var(--page-text-color)"
                  fillOpacity="0.7"
                >
                  {formatClockTime(timestamps[Math.floor(timestamps.length / 2)])}
                </text>
              )}

              <text
                x={PLOT_RIGHT}
                y={VIEW_HEIGHT - 6}
                textAnchor="end"
                fontSize="9"
                fill="var(--page-text-color)"
                fillOpacity="0.7"
              >
                {formatClockTime(lastTime)}
              </text>
            </>
          )}

          {/* Last, so the strips sit above the drawing and take the pointer. */}
          {hasData &&
            timestamps.map((timestamp, index) => {
              const bounds = band(index);

              return (
                <rect
                  key={`band-${timestamp}-${index}`}
                  className="system-stats__hit-band"
                  x={bounds.x}
                  y={PLOT_TOP}
                  width={bounds.width}
                  height={PLOT_HEIGHT}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(index)}
                />
              );
            })}
        </svg>

        {/* Outside the SVG: HTML lays out multi-line text and wraps at the
            container edge, neither of which <text> does without measuring
            glyphs. role="status" announces the reading as the keyboard
            selection moves. */}
        {activeIndex !== null && (
          <div
            className={`system-stats__popover system-stats__popover--${hoverAlign}`}
            style={{ left: `${hoverPercent}%` }}
            role="status"
          >
            <div className="system-stats__popover-time">
              {formatClockTime(timestamps[activeIndex])}
            </div>

            {series.map((entry) => {
              const point = entry.points[activeIndex];
              const readable =
                point === null || point === undefined || !Number.isFinite(point)
                  ? 'No reading'
                  : formatValue(point);

              return (
                <div key={entry.id} className="system-stats__popover-row">
                  <span
                    className="system-stats__swatch"
                    style={{ backgroundColor: entry.color }}
                    aria-hidden="true"
                  />
                  <span className="system-stats__popover-label">{entry.label}</span>
                  <span className="system-stats__popover-value">{readable}</span>
                </div>
              );
            })}

            {(sampleCounts[activeIndex] ?? 1) > 1 && (
              <div className="system-stats__popover-note">
                Average of {sampleCounts[activeIndex]} samples
              </div>
            )}
          </div>
        )}
      </div>

      {/* Outside the SVG so it is selectable and reachable by a screen reader
          as ordinary text. The swatches carry no information the label doesn't. */}
      {series.length > 1 && (
        <ul className="system-stats__legend">
          {series.map((entry) => (
            <li key={entry.id}>
              <span
                className="system-stats__swatch"
                style={{ backgroundColor: entry.color }}
                aria-hidden="true"
              />
              {entry.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
